/* eslint-disable no-restricted-imports */
// "Schedule" on the event form: the event's sessions (a club's meetings, a
// tournament's days). A generator fills an editable table, saved on its own
// with one PUT of the whole list, separately from the event details.
// Contract: zebra-portal-server docs/events-types-sessions-contract.md B.
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import moment from "moment";
import { shallowEqual, useSelector } from "react-redux";
import { IconButton, Snackbar } from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { Spinner } from "react-bootstrap";
import { SnackbarWrapper } from "../../Snackbar/SnackbarWrapper";
import {
  WEEKDAYS,
  buildSessionsPutBody,
  describeGenerator,
  formatSessionDate,
  generateSessions,
  generatorTimes,
  isKeptOnRegenerate,
  newEditorSession,
  regenerateSessions,
  sessionsToEditor,
  sortSessions,
  toHHMM,
  toIsoDate,
  todayIn,
  validateSessionsDefinition
} from "./eventSessions";

const sessionsUrl = "api/workshops/sessions/";
const ATTENDANCE_REASON = "Has attendance — cancel it instead";

// The generator's own checks: at least one weekday, the times (the one pair,
// or each ticked day's with "Different times per day"), and a date range
// inside the event's.
function generatorProblems(
  { times, shared, perDay, from, to },
  { startdate, enddate }
) {
  const errors = [];
  const ticked = WEEKDAYS.filter(w => times[w.day]);
  if (ticked.length === 0) errors.push("Tick at least one weekday");
  if (perDay) {
    ticked.forEach(w => {
      const t = times[w.day];
      if (!t.start || !t.end)
        errors.push(`${w.label}: enter the start and end times`);
      else if (t.end <= t.start)
        errors.push(`${w.label}: the end time must be after the start time`);
    });
  } else if (ticked.length > 0) {
    if (!shared.start || !shared.end)
      errors.push("Enter the From and To times");
    else if (shared.end <= shared.start)
      errors.push("The To time must be after the From time");
  }
  if (!from || !to) errors.push("Enter the first and last dates");
  else if (from > to)
    errors.push("The first date must be on or before the last");
  else if (from < startdate || to > enddate)
    errors.push(
      `Keep the dates inside the event's (${startdate} to ${enddate})`
    );
  return errors;
}

// event: the SAVED event (from the edit endpoint): its startdate / enddate
// bound the sessions (the server checks against the saved dates) and its
// start_time / end_time are the generator's default times.
// selectedType / savedType: event_type rows (GET /api/workshops/event-types)
// for the type chosen on the form and the event's saved type.
// onSummary: called with { status, hasSavedSessions, dirty } whenever those
// change, so the Edit Event page can decide whether its Schedule tab is shown
// (this stays mounted, hidden, while another tab is open).
export function EventScheduleEditor({
  workshopId,
  event,
  selectedType,
  savedType,
  onSummary
}) {
  const authToken = useSelector(state => state.auth.authToken, shallowEqual);
  const branch = useSelector(
    state => state.auth.branches.currentBranch,
    shallowEqual
  );
  const headers = { "x-auth-token": authToken };
  const startdate = toIsoDate(event?.startdate);
  const enddate = toIsoDate(event?.enddate);
  const range = { startdate, enddate };

  // status: "loading" | "error" | "loaded" | "unsupported" (a server
  // without the sessions routes, V101: the page has no Schedule tab)
  const [status, setStatus] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [stored, setStored] = useState([]);
  const [rows, setRows] = useState([]);
  const [savedBody, setSavedBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState([]);
  const [toast, setToast] = useState(null); // { variant, message }
  // What last changed the table since it was loaded or saved, for the
  // unsaved notice: { type: "generated", added } | { type: "regenerated",
  // added, removed, kept } | { type: "edited" }; null when nothing has.
  const [lastAction, setLastAction] = useState(null);
  const noticeRef = useRef(null);

  // Generator (not stored): { [day]: { start, end } } for the ticked weekdays
  // (their own times, used with "Different times per day"), the one time
  // pair for all of them (the event's times to start with), and the dates
  // (the event's to start with).
  const [times, setTimes] = useState({});
  const [shared, setShared] = useState(() => ({
    start: toHHMM(event?.start_time),
    end: toHHMM(event?.end_time)
  }));
  const [perDay, setPerDay] = useState(false);
  const [from, setFrom] = useState(startdate);
  const [to, setTo] = useState(enddate);
  const [skip, setSkip] = useState([]);
  const [skipInput, setSkipInput] = useState("");

  useEffect(() => {
    setFrom(startdate);
    setTo(enddate);
  }, [startdate, enddate]);

  const applyResults = results => {
    const editorRows = sessionsToEditor(results);
    setStored(editorRows);
    setRows(editorRows);
    setSavedBody(JSON.stringify(buildSessionsPutBody(editorRows)));
    setProblems([]);
    setLastAction(null);
  };

  useEffect(() => {
    if (!workshopId) return undefined;
    let cancelled = false;
    setStatus("loading");
    axios(sessionsUrl + workshopId, { headers })
      .then(res => {
        if (cancelled) return;
        applyResults(res.data.results);
        setStatus("loaded");
      })
      .catch(e => {
        console.error("event sessions: ", e);
        if (cancelled) return;
        if (e.response?.status === 404) {
          setStatus("unsupported");
          return;
        }
        setLoadError(e.response?.data?.message || "");
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopId, authToken, reload]);

  // What the tab shows, from the type chosen on the form and the saved one:
  //  "edit"          both have a schedule: generator and table.
  //  "saveTypeFirst" the chosen type has one but the saved type does not:
  //                  the server would refuse a save until the type is saved.
  //  "readOnly"      the chosen type has none but sessions are saved: staff
  //                  can only remove them (those without attendance) and save.
  //  "hidden"        the chosen type has none and no sessions are saved.
  const savedAllows = Number(savedType?.schedule_ind) === 1;
  const selectedAllows = selectedType
    ? Number(selectedType.schedule_ind) === 1
    : savedAllows;
  const hasSavedSessions = stored.length > 0;
  const mode = selectedAllows
    ? savedAllows
      ? "edit"
      : "saveTypeFirst"
    : hasSavedSessions
    ? "readOnly"
    : "hidden";
  const readOnly = mode === "readOnly";
  // The saved type has no schedule: the server takes only an empty list.
  const saveBlocked = readOnly && !savedAllows && rows.length > 0;
  const withAttendance = rows.filter(r => r.attendance_count > 0).length;

  const dirty =
    status === "loaded" &&
    JSON.stringify(buildSessionsPutBody(rows)) !== savedBody;

  useEffect(() => {
    if (onSummary) onSummary({ status, hasSavedSessions, dirty });
  }, [onSummary, status, hasSavedSessions, dirty]);

  // A change made in the table itself (not by the generator).
  const editRows = next => {
    setRows(next);
    setLastAction({ type: "edited" });
  };

  const updateRow = (uid, patch) =>
    editRows(rows.map(r => (r._uid === uid ? { ...r, ...patch } : r)));

  // A newly ticked day starts at the one time pair, and a day whose own
  // times still equal that pair follows it when it changes, so "Different
  // times per day" opens on the pair while keeping times typed there before.
  const toggleDay = day => {
    const next = { ...times };
    if (next[day]) delete next[day];
    else next[day] = { ...shared };
    setTimes(next);
  };

  const changeShared = patch => {
    const next = { ...shared, ...patch };
    const followed = { ...times };
    Object.keys(times).forEach(day => {
      if (times[day].start === shared.start && times[day].end === shared.end)
        followed[day] = { ...next };
    });
    setShared(next);
    setTimes(followed);
  };

  const genTimes = generatorTimes({ times, shared, perDay });
  const genProblems = generatorProblems(
    { times, shared, perDay, from, to },
    range
  );
  const preview = describeGenerator(
    { times: genTimes, from, to, skip },
    range,
    rows.length > 0 ? { rows, today: todayIn(branch?.timezone) } : null
  );
  const tickedDays = WEEKDAYS.filter(w => times[w.day]);

  const generate = () => {
    if (genProblems.length) return;
    const generated = generateSessions({ times: genTimes, from, to, skip });
    if (rows.length === 0) {
      setRows(generated);
      setLastAction({ type: "generated", added: generated.length });
      return;
    }
    const today = todayIn(branch?.timezone);
    const { rows: next, cancelledDates, markedDates } = regenerateSessions(
      rows,
      generated,
      today
    );
    const removed = rows.filter(r => !isKeptOnRegenerate(r, today)).length;
    const added = next.length - (rows.length - removed);
    const dates = list => list.map(formatSessionDate).join(", ");
    if (
      !window.confirm(
        [
          `Regenerate replaces the sessions from today on that have no attendance and aren't cancelled: ${removed} removed, ${added} added.`,
          "Sessions before today, cancelled sessions and sessions with attendance are kept as they are.",
          cancelledDates.length
            ? `No session is added on a cancelled session's date: ${dates(
                cancelledDates
              )}.`
            : "",
          markedDates.length
            ? `No session is added on a date that already has one with attendance: ${dates(
                markedDates
              )}.`
            : "",
          "Nothing is saved until you press Save schedule."
        ]
          .filter(Boolean)
          .join(" ")
      )
    )
      return;
    setRows(next);
    setLastAction({
      type: "regenerated",
      added,
      removed,
      kept: rows.length - removed
    });
  };

  // After Generate / Regenerate the eyes are on the generator above: bring
  // the unsaved notice (and its Save) into view.
  useEffect(() => {
    if (
      lastAction?.type === "generated" ||
      lastAction?.type === "regenerated"
    )
      noticeRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [lastAction]);

  const addSkip = () => {
    if (!skipInput || skip.includes(skipInput)) return;
    setSkip([...skip, skipInput].sort());
    setSkipInput("");
  };

  const addRow = () => {
    const last = sortSessions(rows)[rows.length - 1];
    editRows([
      ...rows,
      newEditorSession(
        last ? last.session_date : startdate,
        last ? last.start_time : toHHMM(event?.start_time),
        last ? last.end_time : toHHMM(event?.end_time)
      )
    ]);
  };

  const loadSaved = async () => {
    const res = await axios(sessionsUrl + workshopId, { headers });
    applyResults(res.data.results);
  };

  // One PUT of the whole schedule. A 400 names the session; a 409 (a
  // removed session got attendance, or the type has no schedule) or a 404
  // "Session N is not on this event" (another tab removed it) means the
  // saved schedule changed or this page is stale, so it is loaded again.
  // The exception is the 409 for an event whose single day already has
  // attendance marks: nothing saved changed, so the edits are kept and the
  // toast says why.
  const save = async () => {
    const errors = validateSessionsDefinition(rows, range);
    setProblems(errors);
    if (errors.length) {
      setToast({
        variant: "error",
        message: "Fix the problems listed below, then save again"
      });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        sessionsUrl + workshopId,
        buildSessionsPutBody(rows),
        { headers }
      );
      applyResults(res.data.results);
      setToast({ variant: "success", message: "Saved the schedule" });
    } catch (e) {
      console.error("save event sessions: ", e);
      let message = e.response?.data?.message || "Couldn't save the schedule";
      const reason = e.response?.data?.message || "";
      const stale =
        (e.response?.status === 409 && !/single day/i.test(reason)) ||
        (e.response?.status === 404 && /not on this event/i.test(reason));
      if (stale) {
        try {
          await loadSaved();
          message +=
            ". The saved schedule was loaded again — make your change from there.";
        } catch (reloadError) {
          console.error("reload event sessions: ", reloadError);
        }
      }
      setToast({ variant: "error", message });
    } finally {
      setSaving(false);
    }
  };

  // In a portal: this tab is display:none while another is open, and a
  // toast that fires then (a slow save) must still show.
  const toastView = ReactDOM.createPortal(
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      open={!!toast}
      autoHideDuration={toast?.variant === "success" ? 3000 : null}
      onClose={(e, reason) => reason !== "clickaway" && setToast(null)}
    >
      <SnackbarWrapper
        onClose={() => setToast(null)}
        variant={toast?.variant || "success"}
        message={toast?.message || ""}
      />
    </Snackbar>,
    document.body
  );

  const sorted = sortSessions(rows);

  // At the top right, and again in the unsaved notice next to its message.
  const saveButton = (
    <button
      type="button"
      className="btn btn-primary btn-sm"
      disabled={!dirty || saving || saveBlocked}
      onClick={save}
    >
      {saving ? "Saving…" : "Save schedule"}
    </button>
  );

  const nothingSaved = "Nothing is saved yet: press Save schedule";
  // Beside a disabled Save, say why rather than "press Save schedule".
  const noticeText = saveBlocked
    ? "Remove every session, then save"
    : lastAction?.type === "generated"
      ? `${lastAction.added} session${
          lastAction.added === 1 ? "" : "s"
        } generated. ${nothingSaved} to keep them.`
      : lastAction?.type === "regenerated"
      ? `Regenerated: ${lastAction.added} session${
          lastAction.added === 1 ? "" : "s"
        } added${lastAction.removed ? `, ${lastAction.removed} removed` : ""}${
          lastAction.kept
            ? `, ${lastAction.kept} earlier, cancelled or marked session${
                lastAction.kept === 1 ? "" : "s"
              } kept`
            : ""
        }. ${nothingSaved}.`
      : "Unsaved changes to the schedule: press Save schedule.";

  return (
    <div>
      {toastView}

      <div className="d-flex align-items-start justify-content-between flex-wrap mb-2">
        <p className="text-muted mb-2 mr-4" style={{ maxWidth: 640 }}>
          The days this event meets, shown on the website and used for
          attendance. Sessions fall between the event&apos;s saved dates (
          {startdate} to {enddate}). Saved here with Save schedule, separately
          from Basic info.
        </p>
        {status === "loaded" && (mode === "edit" || readOnly) && (
          <div className="mb-2">
            {readOnly && (
              <button
                type="button"
                className="btn btn-light-danger btn-sm mr-2"
                disabled={saving || rows.every(r => r.attendance_count > 0)}
                onClick={() =>
                  editRows(rows.filter(r => r.attendance_count > 0))
                }
              >
                Remove sessions
              </button>
            )}
            {saveBlocked && (
              <span className="text-muted mr-2">
                Remove every session, then save
              </span>
            )}
            {saveButton}
          </div>
        )}
      </div>

      {status === "loading" && (
        <div className="d-flex align-items-center text-muted py-3">
          <Spinner animation="border" size="sm" className="mr-2" />
          Loading the schedule…
        </div>
      )}
      {status === "error" && (
        <p className="text-danger">
          Couldn't load the schedule{loadError ? `: ${loadError}` : ""}.{" "}
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={() => setReload(n => n + 1)}
          >
            Try again
          </button>
        </p>
      )}
      {status === "loaded" && mode === "hidden" && (
        <p className="text-muted">
          This event type has no schedule, and this event has no sessions.
        </p>
      )}
      {status === "loaded" && mode === "saveTypeFirst" && (
        <p className="text-muted">
          Save the event with its new type first, then come back to add its
          schedule.
        </p>
      )}

      {status === "loaded" && (mode === "edit" || readOnly) && (
        <>
          {readOnly && (
            <div className="alert alert-custom alert-light-warning mb-4">
              <div className="alert-text">
                {selectedType?.event_type_name || "This"} events have no
                schedule. Remove the sessions with Remove sessions, then save
                {withAttendance > 0 &&
                  ` — ${withAttendance} session${
                    withAttendance === 1 ? " has" : "s have"
                  } attendance and can't be removed, so this event keeps a type with a schedule`}
                .
              </div>
            </div>
          )}

          {mode === "edit" && (
            <div className="border rounded p-4 mb-4">
              <h6 className="mb-3">
                {rows.length === 0
                  ? "Generate sessions"
                  : "Regenerate sessions"}
              </h6>
              <div className="row">
                <div className="col-lg-6">
                  <span id="schedule-weekdays" className="mb-1 d-block">
                    Weekdays
                  </span>
                  <div
                    className="d-flex flex-wrap mb-3"
                    role="group"
                    aria-labelledby="schedule-weekdays"
                  >
                    {WEEKDAYS.map(w => {
                      const on = !!times[w.day];
                      return (
                        <button
                          key={w.day}
                          type="button"
                          className={`btn btn-sm mr-2 mb-2 ${
                            on ? "btn-primary" : "btn-light"
                          }`}
                          style={{ minWidth: 52 }}
                          aria-pressed={on}
                          aria-label={w.label}
                          title={w.label}
                          onClick={() => toggleDay(w.day)}
                        >
                          {w.short}
                        </button>
                      );
                    })}
                  </div>
                  {!perDay ? (
                    <div className="d-flex align-items-end mb-2">
                      <div className="mr-3">
                        <label htmlFor="schedule-time-from" className="mb-1">
                          From
                        </label>
                        <input
                          id="schedule-time-from"
                          type="time"
                          className="form-control form-control-sm"
                          style={{ width: 130 }}
                          value={shared.start}
                          onChange={e => changeShared({ start: e.target.value })}
                        />
                      </div>
                      <div>
                        <label htmlFor="schedule-time-to" className="mb-1">
                          To
                        </label>
                        <input
                          id="schedule-time-to"
                          type="time"
                          className="form-control form-control-sm"
                          style={{ width: 130 }}
                          value={shared.end}
                          onChange={e => changeShared({ end: e.target.value })}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mb-2">
                      {tickedDays.length === 0 && (
                        <p className="text-muted mb-2">
                          Tick a weekday to set its times.
                        </p>
                      )}
                      {tickedDays.map(w => {
                        const t = times[w.day];
                        return (
                          <div
                            key={w.day}
                            className="d-flex align-items-center mb-2"
                          >
                            <span style={{ width: 100 }}>{w.label}</span>
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              style={{ maxWidth: 130 }}
                              aria-label={`${w.label} start time`}
                              value={t.start}
                              onChange={e =>
                                setTimes({
                                  ...times,
                                  [w.day]: { ...t, start: e.target.value }
                                })
                              }
                            />
                            <span className="mx-2">to</span>
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              style={{ maxWidth: 130 }}
                              aria-label={`${w.label} end time`}
                              value={t.end}
                              onChange={e =>
                                setTimes({
                                  ...times,
                                  [w.day]: { ...t, end: e.target.value }
                                })
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="d-flex align-items-center mb-3">
                    <span className="switch switch-sm mr-2">
                      <label>
                        <input
                          id="schedule-per-day"
                          type="checkbox"
                          checked={perDay}
                          onChange={e => setPerDay(e.target.checked)}
                        />
                        <span></span>
                      </label>
                    </span>
                    <label htmlFor="schedule-per-day" className="mb-0">
                      Different times per day
                    </label>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="d-flex mb-3">
                    <div className="mr-3">
                      <label htmlFor="schedule-from" className="mb-1">
                        First date
                      </label>
                      <input
                        id="schedule-from"
                        type="date"
                        className="form-control form-control-sm"
                        min={startdate}
                        max={enddate}
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="schedule-to" className="mb-1">
                        Last date
                      </label>
                      <input
                        id="schedule-to"
                        type="date"
                        className="form-control form-control-sm"
                        min={startdate}
                        max={enddate}
                        value={to}
                        onChange={e => setTo(e.target.value)}
                      />
                    </div>
                  </div>
                  <label htmlFor="schedule-skip" className="mb-1">
                    Skip dates (holidays, breaks)
                  </label>
                  <div className="d-flex align-items-center mb-2">
                    <input
                      id="schedule-skip"
                      type="date"
                      className="form-control form-control-sm"
                      style={{ maxWidth: 170 }}
                      min={startdate}
                      max={enddate}
                      value={skipInput}
                      onChange={e => setSkipInput(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-light btn-sm ml-2"
                      disabled={!skipInput}
                      onClick={addSkip}
                    >
                      Add
                    </button>
                  </div>
                  <div>
                    {skip.map(d => (
                      <span
                        key={d}
                        className="label label-inline label-light-primary mr-2 mb-2"
                      >
                        {formatSessionDate(d)}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ml-2"
                          aria-label={`Don't skip ${d}`}
                          onClick={() => setSkip(skip.filter(s => s !== d))}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {/* "Tick at least one weekday" is the preview's to say. */}
              {tickedDays.length > 0 && genProblems.length > 0 && (
                <ul className="text-danger mb-2 pl-4">
                  {genProblems.map(p => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}
              <p
                id="schedule-preview"
                className="font-weight-bold mb-2"
                aria-live="polite"
              >
                {preview.sentence}
              </p>
              <button
                type="button"
                className="btn btn-light-primary btn-sm"
                aria-describedby="schedule-preview"
                disabled={
                  saving ||
                  genProblems.length > 0 ||
                  (rows.length === 0 && preview.count === 0)
                }
                onClick={generate}
              >
                {rows.length > 0
                  ? "Regenerate"
                  : preview.count > 0
                  ? `Generate ${preview.count} session${
                      preview.count === 1 ? "" : "s"
                    }`
                  : "Generate"}
              </button>
            </div>
          )}

          {dirty && (
            <div
              ref={noticeRef}
              className="alert alert-custom alert-light-warning d-flex align-items-center flex-wrap mb-4"
              role="status"
            >
              <div className="alert-text mr-3">{noticeText}</div>
              <div>{saveButton}</div>
            </div>
          )}

          {problems.length > 0 && (
            <div className="alert alert-custom alert-light-danger mb-4">
              <div className="alert-text">
                <ul className="mb-0 pl-4">
                  {problems.map(p => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {rows.length === 0 ? (
            <p className="text-muted">
              No sessions — the event&apos;s own dates and times are its one
              session, exactly as before.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-vertical-center">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Start</th>
                    <th>End</th>
                    <th className="text-center">Cancelled</th>
                    <th>Note</th>
                    <th className="text-center">Attendance</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(r => (
                    <tr
                      key={r._uid}
                      style={{
                        opacity: r.cancelled_ind ? 0.6 : 1,
                        textDecoration: r.cancelled_ind
                          ? "line-through"
                          : undefined
                      }}
                    >
                      <td style={{ whiteSpace: "nowrap" }}>
                        <input
                          type="date"
                          className="form-control form-control-sm d-inline-block"
                          style={{ width: 160 }}
                          aria-label="Session date"
                          min={startdate}
                          max={enddate}
                          value={r.session_date}
                          disabled={readOnly}
                          onChange={e =>
                            updateRow(r._uid, { session_date: e.target.value })
                          }
                        />
                        <span className="text-muted ml-2">
                          {r.session_date
                            ? moment(r.session_date, "YYYY-MM-DD").format("ddd")
                            : ""}
                        </span>
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          style={{ width: 120 }}
                          aria-label="Start time"
                          value={r.start_time}
                          disabled={readOnly}
                          onChange={e =>
                            updateRow(r._uid, { start_time: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          style={{ width: 120 }}
                          aria-label="End time"
                          value={r.end_time}
                          disabled={readOnly}
                          onChange={e =>
                            updateRow(r._uid, { end_time: e.target.value })
                          }
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          aria-label="Cancelled"
                          checked={!!r.cancelled_ind}
                          disabled={readOnly}
                          onChange={e =>
                            updateRow(r._uid, {
                              cancelled_ind: e.target.checked ? 1 : 0
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          style={{ minWidth: 160 }}
                          aria-label="Note"
                          maxLength={200}
                          placeholder="e.g. Room 2"
                          value={r.note}
                          disabled={readOnly}
                          onChange={e =>
                            updateRow(r._uid, { note: e.target.value })
                          }
                        />
                      </td>
                      <td className="text-center">
                        {r.attendance_count > 0
                          ? `${r.attendance_count} marked`
                          : "—"}
                      </td>
                      <td>
                        <span
                          title={
                            r.attendance_count > 0
                              ? ATTENDANCE_REASON
                              : "Delete session"
                          }
                        >
                          <IconButton
                            size="small"
                            aria-label={
                              r.attendance_count > 0
                                ? ATTENDANCE_REASON
                                : "Delete session"
                            }
                            disabled={r.attendance_count > 0}
                            onClick={() =>
                              editRows(rows.filter(x => x._uid !== r._uid))
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {mode === "edit" && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mb-3"
              onClick={addRow}
            >
              + Add session
            </button>
          )}
          {rows.length > 0 && (
            <p className="text-muted mb-1">
              {rows.filter(r => !r.cancelled_ind).length} session
              {rows.filter(r => !r.cancelled_ind).length === 1 ? "" : "s"}
              {rows.some(r => r.cancelled_ind) &&
                `, ${rows.filter(r => r.cancelled_ind).length} cancelled`}
            </p>
          )}
        </>
      )}
    </div>
  );
}
